import { Add, Description } from '@mui/icons-material';
import { FormControlLabel, Switch, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  DataGrid,
  DataGridProps,
  ptBR,
  GridPanelWrapper,
  GridPanelHeader,
  GridPanelContent,
  GridPanelFooter,
  useGridApiContext,
  GridStateColDef,
  useGridSelector,
  gridColumnVisibilityModelSelector,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import { useEffect, useState, useMemo, useRef } from 'react';
import Button from './Button';
import Input from './Input';
import Spacer from './Spacer';
import { useStorage } from '../hooks';

const useStyles = makeStyles(({ spacing, palette }: Theme) => ({
  table: {
    background: palette.neutral[400],
  },
  content: {
    padding: spacing(),
  },
  addItemContainer: {
    color: palette.primary.main,
  },
}));

const CustomColumnsPanel = (): JSX.Element => {
  const apiRef = useGridApiContext();
  const {
    current: { getAllColumns, setColumnVisibility },
  } = apiRef;

  const [columns, setColumns] = useState<GridStateColDef[]>([]);
  const [search, setSearch] = useState('');
  const styles = useStyles();

  const columnVisibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector);

  const filterdColumns = useMemo(() => {
    if (!search) {
      return columns;
    }

    return columns.filter((column) => column.headerName?.toLowerCase().includes(search.toLowerCase()));
  }, [columns, search]);

  useEffect(() => {
    const allColumns = getAllColumns();
    const columnsToShow = allColumns.filter((column) => column.hideable === undefined || column.hideable);

    setColumns(columnsToShow);
  }, [getAllColumns]);

  const toggleAll = (showAll: boolean) => {
    const allColumns = getAllColumns();

    allColumns.forEach((column) => {
      setColumnVisibility(column.field, showAll);
    });
  };

  return (
    <GridPanelWrapper>
      <GridPanelHeader>
        <Input
          label="Digite a coluna"
          variant="standard"
          placeholder="Digite a coluna para esconder"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </GridPanelHeader>
      <GridPanelContent className={styles.content}>
        {filterdColumns.map((column) => (
          <FormControlLabel
            label={column.headerName}
            key={column.field}
            control={
              <Switch
                checked={columnVisibilityModel[column.field] !== false}
                onChange={(e, checked) => setColumnVisibility(column.field, checked)}
              />
            }
          />
        ))}
      </GridPanelContent>
      <GridPanelFooter>
        <Button variant="text" color="primary" onClick={() => toggleAll(true)}>
          MOSTRAR TODAS
        </Button>
        <Button variant="text" color="primary" onClick={() => toggleAll(false)}>
          ESCONDER TODAS
        </Button>
      </GridPanelFooter>
    </GridPanelWrapper>
  );
};

interface GridToolBarAddProps {
  onClick?: (data?: unknown) => unknown;
}

const GridToolbarAdd = ({ onClick }: GridToolBarAddProps): JSX.Element => (
  <Button size="small" variant="text" color="primary" height="32px" startIcon={<Add />} onClick={onClick}>
    ADICIONAR
  </Button>
);

interface GridToolbarImportProps {
  onImport?: (input: string) => Promise<void>; // Ajuste para garantir que `input` não seja opcional
}

const GridToolbarImport = ({ onImport }: GridToolbarImportProps): JSX.Element => {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const storage = useStorage();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const url = await storage.upload(`/sectionsunitsimports/${file.name}`, file);
      const sectionsUnitsData: string = url;
      if (onImport) {
        await onImport(sectionsUnitsData);
      }
    }
  };

  const handleClick = () => {
    if (inputFileRef.current) {
      inputFileRef.current.click();
    }
  };

  return (
    <>
      <Button
        size="small"
        variant="text"
        color="primary"
        height="32px"
        startIcon={<Description />}
        onClick={handleClick}
      >
        IMPORTAR CSV
      </Button>
      <input
        type="file"
        ref={inputFileRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </>
  );
};


interface CustomGridToolbarProps {
  showAddButton?: boolean;
  showOnChangeImport?: boolean;
  onClickAdd?: (input?: unknown) => unknown;
  onChangeImport?: (input: string) => Promise<void>;
}

const CustomGridToolbar = ({ showAddButton, showOnChangeImport, onClickAdd, onChangeImport }: CustomGridToolbarProps): JSX.Element => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton onResize={undefined} onResizeCapture={undefined} />
    <GridToolbarFilterButton onResize={undefined} onResizeCapture={undefined} />
    <GridToolbarDensitySelector onResize={undefined} onResizeCapture={undefined} />
    <GridToolbarExport />
    <Spacer />
    {showOnChangeImport && <GridToolbarImport onImport={onChangeImport} />}
    {showAddButton && <GridToolbarAdd onClick={onClickAdd} />}
  </GridToolbarContainer>
);

export interface CustomDataTableProps extends DataGridProps, CustomGridToolbarProps {}

export const DataTable = ({
  sx,
  disableSelectionOnClick,
  onClickAdd,
  onChangeImport,
  showOnChangeImport = false,
  showAddButton = false,
  ...props
}: CustomDataTableProps): JSX.Element => {
  const styles = useStyles();
  return (
    <DataGrid
      className={styles.table}
      {...props}
      disableSelectionOnClick={disableSelectionOnClick ?? true}
      localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
      sx={{
        ...sx,
        '& ::-webkit-scrollbar': {
          height: '8px',
          width: '6px',
        },
        '& ::-webkit-scrollbar-track-piece': ({ palette }) => ({
          background: palette.neutral[300],
        }),
        '& ::-webkit-scrollbar-thumb:vertical, & ::-webkit-scrollbar-thumb:horizontal': ({ palette }) => ({
          background: palette.neutral[400],
          borderRadius: '20px',
        }),
      }}
      components={{
        ColumnsPanel: CustomColumnsPanel,
        Toolbar: () => <CustomGridToolbar onClickAdd={onClickAdd} showAddButton={showAddButton} onChangeImport={onChangeImport} showOnChangeImport={showOnChangeImport} />,
      }}
    />
  );
};

export default DataTable;
